import { useEffect, useMemo } from 'react';
import { HiArrowRight, HiDownload, HiStar } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { downloadCsv } from '../../utils/csvDownload';
import { useDispatch, useSelector } from 'react-redux';
import WidgetWrapper from '../WidgetWrapper';
import { DashboardP2Insights } from '../../types/dashboard.types';
import { AppDispatch, RootState } from '../../state/store';
import { fetchReferralLeaderboard } from '../../services/DashboardService';
import { AppRoutes } from '../../types/constants.types';

type DashboardReferralIntelligenceWidgetProps = {
  insights: DashboardP2Insights | null;
  compareInsights?: DashboardP2Insights | null;
  compareLabel?: string;
  loading?: boolean;
  fromDate: string;
  toDate: string;
};

export default function DashboardReferralIntelligenceWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
  fromDate,
  toDate,
}: DashboardReferralIntelligenceWidgetProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { referralLeaderboard, referralLeaderboardLoading } = useSelector(
    (state: RootState) => state.dashboard,
  );
  const current = insights?.referralIntelligence;
  const compare = compareInsights?.referralIntelligence;

  const mergedReferrers = useMemo(() => {
    const intelligenceByUserId = new Map((current?.topReferrers ?? []).map((item) => [item.userId, item]));

    return referralLeaderboard.map((item) => {
      const intelligence = intelligenceByUserId.get(item.userId);
      return {
        userId: item.userId,
        userName: item.userName,
        email: item.email,
        referralCode: item.referralCode || intelligence?.referralCode || null,
        totalReferrals: item.totalReferrals,
        referralSignups: intelligence?.referralSignups ?? 0,
        referralDonors: intelligence?.referralDonors ?? 0,
        conversionRatePct: intelligence?.conversionRatePct ?? 0,
      };
    });
  }, [current?.topReferrers, referralLeaderboard]);

  const topPerformer = mergedReferrers[0] ?? null;

  const handleDownload = () => {
    if (!mergedReferrers.length) return;
    downloadCsv('referral-performance', mergedReferrers.map((item, i) => ({
      Rank: i + 1,
      Name: item.userName,
      Email: item.email ?? '',
      'Referral Code': item.referralCode ?? '',
      'Total Referrals': item.totalReferrals,
      'Referral Signups': item.referralSignups,
      'Referred Donors': item.referralDonors,
      'Conversion Rate': `${item.conversionRatePct.toFixed(2)}%`,
    })));
  };

  useEffect(() => {
    dispatch(fetchReferralLeaderboard({
      limit: 5,
      fromDate,
      toDate,
    }));
  }, [dispatch, fromDate, toDate]);

  const navigateToReferrals = () => {
    navigate(AppRoutes.REFERRALS);
  };

  return (
    <WidgetWrapper
      title="Referral Growth"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-pink-50/80 via-white/75 to-rose-100/75 dark:from-pink-900/20 dark:via-slate-900/78 dark:to-rose-900/18 border-pink-200/50 dark:border-pink-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between rounded-2xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/50">
            <div>
              <p className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                Referral performance overview
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                One ranked view of referrals, signups, donors, and conversion for the selected period.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                disabled={!mergedReferrers.length}
                title="Download CSV"
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold text-fuchsia-700 transition-colors hover:bg-fuchsia-100 dark:text-fuchsia-200 dark:hover:bg-fuchsia-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiDownload className="h-4 w-4" />
                <span>CSV</span>
              </button>
              <button
                onClick={navigateToReferrals}
                className="flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-fuchsia-700 transition-colors hover:bg-fuchsia-100 dark:text-fuchsia-200 dark:hover:bg-fuchsia-900/30"
              >
                <span>View all</span>
                <HiArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
            <MetricCard label="Active referrers" value={current?.summary.activeReferrers ?? 0} compareValue={compare?.summary.activeReferrers} compareLabel={compareLabel} />
            <MetricCard label="Referral signups" value={current?.summary.totalReferralSignups ?? 0} compareValue={compare?.summary.totalReferralSignups} compareLabel={compareLabel} />
            <MetricCard label="Referred donors" value={current?.summary.referredDonors ?? 0} compareValue={compare?.summary.referredDonors} compareLabel={compareLabel} />
            <MetricCard label="Signup → donor" value={`${(current?.summary.referralToDonorRatePct ?? 0).toFixed(2)}%`} compareValue={compare ? `${compare.summary.referralToDonorRatePct.toFixed(2)}%` : undefined} compareLabel={compareLabel} />
          </div>

          {topPerformer && !referralLeaderboardLoading && (
            <div className="relative overflow-hidden rounded-2xl border border-amber-300/60 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-4 shadow-md dark:border-amber-400/20 dark:from-amber-900/25 dark:via-yellow-900/15 dark:to-orange-900/20">
              <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-full bg-amber-300/20 blur-2xl dark:bg-amber-400/10" />
              <div className="pointer-events-none absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-orange-300/20 blur-2xl dark:bg-orange-400/10" />

              <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-400/30 text-amber-600 dark:bg-amber-400/20 dark:text-amber-300">
                    <HiStar className="h-7 w-7" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">
                      Top performer
                    </p>
                    <p className="truncate text-base font-bold text-gray-900 dark:text-gray-100">
                      {topPerformer.userName}
                    </p>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {topPerformer.email}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <HighlightStat label="Referrals" value={topPerformer.totalReferrals.toLocaleString()} />
                  <HighlightStat label="Signups" value={topPerformer.referralSignups.toLocaleString()} />
                  <HighlightStat label="Donors" value={topPerformer.referralDonors.toLocaleString()} />
                  <HighlightStat label="Conversion" value={`${topPerformer.conversionRatePct.toFixed(2)}%`} />
                </div>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 overflow-hidden">
            {referralLeaderboardLoading ? (
              <div className="grid gap-2 p-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-16 animate-pulse rounded-xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45"
                  />
                ))}
              </div>
            ) : mergedReferrers.length > 0 ? (
              <table className="w-full table-auto border-collapse">
                <thead className="bg-primary-50/80 dark:bg-slate-800/80">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Referrer</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Referrals</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Signups</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Donors</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Signup → donor</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedReferrers.map((item, index) => (
                    <tr key={item.userId} className="border-t border-gray-200/60 dark:border-white/10">
                      <td className="px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-fuchsia-500/15 text-[11px] font-bold text-fuchsia-700 dark:text-fuchsia-200">
                            #{index + 1}
                          </span>
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate">{item.userName}</span>
                            <span className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                              {item.referralCode || '—'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">{item.totalReferrals.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{item.referralSignups.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{item.referralDonors.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">{item.conversionRatePct.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/60 bg-white/60 px-4 py-8 text-center text-sm text-gray-600 dark:border-white/10 dark:bg-slate-900/45 dark:text-gray-300">
                No referral performance data is available yet.
              </div>
            )}
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
}

type MetricCardProps = {
  label: string;
  value: string | number;
  compareValue?: string | number;
  compareLabel: string;
};

function MetricCard({ label, value, compareValue, compareLabel }: MetricCardProps) {
  return (
    <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</p>
      {compareValue !== undefined && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareValue}</p>
      )}
    </div>
  );
}

type HighlightStatProps = {
  label: string;
  value: string;
};

function HighlightStat({ label, value }: HighlightStatProps) {
  return (
    <div className="rounded-xl border border-amber-300/40 bg-white/50 px-3 py-2 text-center dark:border-amber-400/15 dark:bg-slate-900/30">
      <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}
